import { cn } from '@/lib/tailwind.utils'

export type GuidePageHeaderProps = {
  header?: string
  subheader?: string
  backgroundImageUrl?: string
  heroImageUrl?: string
  heroImageAlt?: string
}

/**
 * GuidePageHeader component displays a header, optional subheader, and an optional hero image.
 *
 * @param header - Main heading text.
 * @param subheader - Optional subheading text.
 * @param heroImageUrl - If provided, expands the banner significantly and displays the image on the right side.
 * @param heroImageAlt - Alt text for the hero image.
 * @returns React component rendering the banner.
 */
export function GuidePageHeader({
  header,
  subheader,
  heroImageUrl,
  heroImageAlt,
}: GuidePageHeaderProps) {
  const expanded = !!heroImageUrl

  return (
    <div
      className={cn(
        'relative flex justify-center py-24 text-left text-base text-foreground',
        '[&>*]:z-10',
        expanded ? 'pb-32' : 'pb-24',
      )}
    >
      <div className="flex w-[1184px] max-w-full flex-col gap-8">
        <div className="flex">
          <div className="flex min-w-0 flex-1 flex-col justify-center [&_p]:mt-2 [&_p]:text-[22px] [&_p]:font-normal">
            <h1 className="text-[30px] font-bold leading-[1.2] text-darkBlue dark:text-foreground ">
              {header}
            </h1>
            {subheader && <p>{subheader}</p>}
          </div>
          {heroImageUrl && (
            <div className="flex min-w-[54%] flex-1 flex-col justify-center [&_img]:h-auto [&_img]:min-h-[400px] [&_img]:w-full [&_img]:object-contain">
              <img src={heroImageUrl} alt={heroImageAlt} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
